<template>
    <div class="dashboard-page">

        <section class="dashboard-header">
            <div class="dashboard-title">
                <span class="dashboard-badge">Painel do Serviço de Inspeção</span>
                <h1>Resumo geral</h1>
                <p>Indicadores e pendências do serviço de inspeção.</p>
            </div>

            <div class="dashboard-totais">
                <div class="dashboard-total-card">
                    <span class="total-icon">🏢</span>
                    <div>
                        <strong>{{ stats.ttestabelecimentos }}</strong>
                        <small>Estabelecimentos</small>
                    </div>
                </div>

                <div class="dashboard-total-card">
                    <span class="total-icon">🧪</span>
                    <div>
                        <strong>{{ stats.ttcoletas }}</strong>
                        <small>Coletas</small>
                    </div>
                </div>

                <div class="dashboard-total-card">
                    <span class="total-icon">🔍</span>
                    <div>
                        <strong>{{ stats.ttvistorias }}</strong>
                        <small>Vistorias</small>
                    </div>
                </div>
            </div>
        </section>

        <section v-if="pendenciasAtivas.length" class="pendencias-card">
            <div class="pendencias-title">
                <strong>Ações pendentes</strong>
                <span>Itens aguardando atenção</span>
            </div>

            <div class="pendencias-grid">
                <div 
                    v-for="item in pendenciasAtivas" 
                    :key="item.key"
                    class="pendencia-item"
                >
                    <div class="pendencia-label">
                        <span>{{ item.icon }}</span>
                        <strong>{{ item.titulo }}</strong>
                    </div>

                    <div class="pendencia-total">
                        {{ item.total }}
                    </div>
                </div>
            </div>
        </section>

        <section class="charts-grid">
            <div class="chart-card">
                <div class="chart-header">
                    <h3>Vistorias x Não conformidades</h3>
                    <span>Últimos 12 meses</span>
                </div>

                <ChartNcMensalPage :data="chartData" />
            </div>

            <div class="chart-card">
                <div class="chart-header">
                    <h3>Coletas x Não conformidades</h3>
                    <span>Últimos 12 meses</span>
                </div>

                <ChartColetaMensalPage :data="chartColetaData" />
            </div>
        </section>

    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { globalStore } from '@/globals';
import ChartColetaMensalPage from './ChartColetaMensalPage.vue';
import ChartNcMensalPage from './ChartNcMensalPage.vue';

export default{
    components: { ChartNcMensalPage, ChartColetaMensalPage },

    setup(){
        const stats = ref({
            ttestabelecimentos: 0,
            ttcoletas: 0,
            ttvistorias: 0,
        });

        const pendencias = ref({
            ttassinaturas: 0,
            ttprocessos: 0,
            ttrac: 0,
            ttdocumentos: 0,
        });

        const chartData = ref([]);
        const chartColetaData = ref([]);

        const pendenciasAtivas = computed(() => {
            const itens = [
                {
                    key: 'ttassinaturas',
                    total: pendencias.value.ttassinaturas,
                    icon: '✍️',
                    titulo: 'Assinaturas',
                },
                {
                    key: 'ttprocessos',
                    total: pendencias.value.ttprocessos,
                    icon: '📌',
                    titulo: 'Processos',
                },
                {
                    key: 'ttrac',
                    total: pendencias.value.ttrac,
                    icon: '🛠️',
                    titulo: 'Ações corretivas',
                },
                {
                    key: 'ttdocumentos',
                    total: pendencias.value.ttdocumentos,
                    icon: '📄',
                    titulo: 'Documentos',
                },
            ];

            return itens.filter(item => Number(item.total) > 0);
        });

        const loadStats = async () => {
            try{
                const response = await Api.genericPost('/dados/resumo_geral', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                });

                stats.value = Array.isArray(response) && response.length 
                    ? response[0] 
                    : stats.value;

                const req = await Api.genericPost('/dados/usuario/resumo/pendencias', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                    idestabelecimento: globalStore.idestabelecimento,
                    idusuario: globalStore.idusuario,
                });

                pendencias.value = Array.isArray(req) && req.length 
                    ? req[0] 
                    : pendencias.value;

            }catch(error){
                console.error('Erro ao carregar dados de estatísticas:', error);
            }
        };

        const getPeriodo = () => {
            const now = new Date();

            const inicio = new Date(now.getFullYear(), now.getMonth() - 11, 1);
            const fim = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            return {
                datainicial: inicio.toISOString().split('T')[0],
                datafinal: fim.toISOString().split('T')[0],
            };
        };

        const loadChartData = async () => {
            try{
                const periodo = getPeriodo();

                const rVist = await Api.genericPost('/dados/vistoria/ttmensal', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                    ...periodo,
                });

                const rVistNC = await Api.genericPost('/dados/vistoria/ttnc/mensal', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                    ...periodo,
                });

                chartData.value = [
                    rVist.map(item => ({
                        x: `${item.ano}-${String(item.mes).padStart(2, '0')}`,
                        y: item.qtdd,
                    })),
                    rVistNC.map(item => ({
                        x: `${item.ano}-${String(item.mes).padStart(2, '0')}`,
                        y: item.qtdd,
                    })),
                ];

            }catch(error){
                console.error('Erro ao carregar dados do gráfico:', error);
            }
        };

        const loadChartColetaData = async () => {
            try{
                const periodo = getPeriodo();

                const rColeta = await Api.genericPost('/dados/coleta/ttmensal', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                    ...periodo,
                });

                const rNcColeta = await Api.genericPost('/dados/coleta/ttnc/mensal', {
                    idep: globalStore.idep,
                    idsim: globalStore.idsim,
                    ...periodo,
                });

                chartColetaData.value = [
                    rColeta.map(item => ({
                        x: `${item.ano}-${String(item.mes).padStart(2, '0')}`,
                        y: item.qtdd,
                    })),
                    rNcColeta.map(item => ({
                        x: `${item.ano}-${String(item.mes).padStart(2, '0')}`,
                        y: item.qtdd,
                    })),
                ];

            }catch(error){
                console.error('Erro ao carregar dados do gráfico:', error);
            }
        };

        onMounted(() => {
            loadStats();
            loadChartData();
            loadChartColetaData();
        });

        return {
            stats,
            pendencias,
            pendenciasAtivas,
            chartData,
            chartColetaData,
        };
    }
}
</script>

<style scoped>
.dashboard-page{
    padding:14px 20px;
}

.dashboard-header{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:20px;

    background:linear-gradient(135deg,#f8fafc,#eef2ff);
    border:1px solid #dbe3f0;
    border-radius:12px;

    padding:14px 18px;
    margin-bottom:12px;

    box-shadow:0 3px 10px rgba(15,23,42,.05);
}

.dashboard-title{
    text-align:left;
}

.dashboard-badge{
    display:inline-block;
    background:#e0e7ff;
    color:#3730a3;
    font-size:11px;
    font-weight:700;
    padding:4px 9px;
    border-radius:999px;
    margin-bottom:5px;
}

.dashboard-title h1{
    margin:0;
    color:#1f2937;
    font-size:22px;
    line-height:1.15;
}

.dashboard-title p{
    margin:3px 0 0;
    color:#4b5563;
    font-size:13px;
}

.dashboard-totais{
    display:flex;
    gap:10px;
    flex-wrap:wrap;
    justify-content:flex-end;
}

.dashboard-total-card{
    display:flex;
    align-items:center;
    gap:9px;

    min-width:145px;
    padding:9px 11px;

    background:#fff;
    border:1px solid #e5e7eb;
    border-radius:10px;

    box-shadow:0 2px 6px rgba(15,23,42,.04);
    box-sizing:border-box;
}

.total-icon{
    width:34px;
    height:34px;
    border-radius:10px;

    display:flex;
    align-items:center;
    justify-content:center;

    background:#f0fdfa;
    font-size:20px;
    flex-shrink:0;
}

.dashboard-total-card strong{
    display:block;
    color:#111827;
    font-size:22px;
    line-height:1;
}

.dashboard-total-card small{
    display:block;
    color:#6b7280;
    font-size:12px;
    margin-top:2px;
    white-space:nowrap;
}

.pendencias-card{
    display:flex;
    align-items:center;
    gap:14px;

    background:#fff7ed;
    border:1px solid #fed7aa;
    border-radius:12px;

    padding:10px 12px;
    margin-bottom:12px;

    box-shadow:0 2px 8px rgba(15,23,42,.04);
}

.pendencias-title{
    min-width:190px;
    text-align:left;
}

.pendencias-title strong{
    display:block;
    color:#7c2d12;
    font-size:15px;
}

.pendencias-title span{
    display:block;
    color:#9a3412;
    font-size:12px;
    margin-top:2px;
}

.pendencias-grid{
    flex:1;
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(160px,1fr));
    gap:8px;
}

.pendencia-item{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:8px;

    background:#fff;
    border:1px solid #fed7aa;
    border-radius:9px;

    padding:8px 10px;
    min-height:42px;
    box-sizing:border-box;
}

.pendencia-label{
    display:flex;
    align-items:center;
    gap:7px;
    min-width:0;
}

.pendencia-label span{
    font-size:17px;
    flex-shrink:0;
}

.pendencia-label strong{
    color:#1f2937;
    font-size:13px;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
}

.pendencia-total{
    min-width:26px;
    height:26px;
    border-radius:999px;

    background:#ea580c;
    color:#fff;

    display:flex;
    align-items:center;
    justify-content:center;

    font-weight:700;
    font-size:12px;
    flex-shrink:0;
}

.charts-grid{
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(360px,1fr));
    gap:14px;
}

.chart-card{
    background:#fff;
    border:1px solid #e5e7eb;
    border-radius:12px;

    padding:12px 14px;

    box-shadow:0 2px 8px rgba(15,23,42,.04);
    min-width:0;
}

.chart-header{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    margin-bottom:6px;
}

.chart-header h3{
    margin:0;
    color:#1f2937;
    font-size:15px;
}

.chart-header span{
    color:#6b7280;
    font-size:12px;
    white-space:nowrap;
}

@media (max-width:900px){
    .dashboard-header{
        align-items:flex-start;
        flex-direction:column;
    }

    .dashboard-totais{
        width:100%;
        justify-content:stretch;
    }

    .dashboard-total-card{
        flex:1;
        min-width:160px;
    }

    .pendencias-card{
        align-items:stretch;
        flex-direction:column;
    }

    .pendencias-title{
        min-width:0;
    }
}

@media (max-width:600px){
    .dashboard-page{
        padding:10px;
    }

    .charts-grid{
        grid-template-columns:1fr;
    }

    .chart-header{
        align-items:flex-start;
        flex-direction:column;
    }
}
</style>